import { Panel, PageHeader } from "@/components/admin-shell";
import { apiRequest } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Star,
  Check,
  X,
  Trash2,
  ShieldCheck,
  Heart,
  Sparkles,
  MessageSquare,
  AlertCircle,
  Plus,
  Loader2,
  Upload,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface IReview {
  _id: string;
  productId: {
    _id: string;
    name: { bn: string; en: string };
    slug: string;
    image?: string;
  } | null;
  productSlug?: string;
  userName: string;
  userLocation?: string;
  rating: number;
  title?: string;
  body: string;
  photos?: string[];
  status: "pending" | "published" | "rejected";
  featured?: boolean;
  createdAt: string;
}

interface IProduct {
  _id: string;
  slug: string;
  name: { bn: string; en: string };
  image?: string;
  images?: string[];
}

export default function ReviewsPage() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<"all" | "pending" | "published" | "rejected" | "featured">(
    "all",
  );

  // Modal & Form State
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [userName, setUserName] = useState("");
  const [userLocation, setUserLocation] = useState("");
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Queries
  const {
    data: reviews = [],
    isLoading,
    error,
  } = useQuery<IReview[]>({
    queryKey: ["reviews"],
    queryFn: () => apiRequest<IReview[]>("/reviews?all=true"),
  });

  const { data: products = [] } = useQuery<IProduct[]>({
    queryKey: ["products", "admin-list"],
    queryFn: () => apiRequest<IProduct[]>("/products?isAdmin=true"),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (payload: any) => apiRequest("/reviews", "POST", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      toast.success("Review created successfully!");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create review");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<IReview> }) =>
      apiRequest(`/reviews/${id}`, "PATCH", payload),
    onSuccess: (data: any, variables) => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      const statusMsg = variables.payload.status
        ? `Status updated to ${variables.payload.status}`
        : variables.payload.featured !== undefined
          ? `Featured status updated`
          : "Review updated successfully";
      toast.success(statusMsg);
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update review");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest(`/reviews/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      toast.success("Review deleted successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete review");
    },
  });

  // Event Handlers
  const handleStatusChange = (id: string, newStatus: "published" | "rejected") => {
    updateMutation.mutate({ id, payload: { status: newStatus } });
  };

  const handleToggleFeatured = (id: string, currentFeatured: boolean) => {
    updateMutation.mutate({ id, payload: { featured: !currentFeatured } });
  };

  const handleDelete = (id: string) => {
    if (
      window.confirm(
        "Are you sure you want to permanently delete this review? This will also update the product's average rating.",
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setSelectedProductId("");
    setUserName("");
    setUserLocation("");
    setRating(5);
    setTitle("");
    setBody("");
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProductId) {
      toast.error("Please select a product");
      return;
    }
    if (!userName.trim()) {
      toast.error("Please enter reviewer's name");
      return;
    }
    if (!body.trim()) {
      toast.error("Please enter review body");
      return;
    }

    let photos: string[] = [];
    if (imageFile) {
      setIsUploading(true);
      const toastId = toast.loading("Uploading review photo...");
      const formData = new FormData();
      formData.append("file", imageFile);
      formData.append("folder", "subaashghor/reviews");

      try {
        const response = await apiRequest<{ url: string }>("/uploads", "POST", formData);
        photos = [response.url];
        toast.success("Photo uploaded successfully", { id: toastId });
      } catch (err: any) {
        toast.error(err.message || "Failed to upload photo", { id: toastId });
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    createMutation.mutate({
      productId: selectedProductId,
      userName: userName.trim(),
      userLocation: userLocation.trim() || undefined,
      rating,
      title: title.trim() || undefined,
      body: body.trim(),
      photos,
      status: "published", // Automatically publish admin-created reviews
    });
  };

  // Filter logic
  const filteredReviews = reviews.filter((r) => {
    if (filter === "pending") return r.status === "pending";
    if (filter === "published") return r.status === "published";
    if (filter === "rejected") return r.status === "rejected";
    if (filter === "featured") return !!r.featured;
    return true;
  });

  return (
    <>
      <PageHeader
        title="Product Reviews"
        subtitle="Moderate customer feedback, approve ratings, and manage featured testimonials."
        action={
          <button
            onClick={() => setIsOpen(true)}
            className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 transition cursor-pointer"
          >
            <Plus className="h-4 w-4" /> Add Review
          </button>
        }
      />

      {/* Stats Summary Panel */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Panel className="flex items-center gap-4 border border-gold/15 bg-card/40 p-5">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gold/10 text-gold">
            <MessageSquare className="h-6 w-6" />
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">{reviews.length}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">
              Total Reviews
            </div>
          </div>
        </Panel>

        <Panel className="flex items-center gap-4 border border-gold/15 bg-card/40 p-5">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
            <AlertCircle className="h-6 w-6 animate-pulse" />
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">
              {reviews.filter((r) => r.status === "pending").length}
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">
              Pending Approval
            </div>
          </div>
        </Panel>

        <Panel className="flex items-center gap-4 border border-gold/15 bg-card/40 p-5">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">
              {reviews.filter((r) => r.status === "published").length}
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">
              Published Reviews
            </div>
          </div>
        </Panel>

        <Panel className="flex items-center gap-4 border border-gold/15 bg-card/40 p-5">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-pink-500/10 text-pink-500">
            <Heart className="h-6 w-6" />
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">
              {reviews.filter((r) => !!r.featured).length}
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">
              Featured Testimonials
            </div>
          </div>
        </Panel>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex flex-wrap gap-2 border-b border-gold/15 pb-4">
        {(
          [
            ["all", "All Reviews"],
            ["pending", "Pending Approval"],
            ["published", "Published"],
            ["rejected", "Rejected"],
            ["featured", "Featured"],
          ] as const
        ).map(([k, label]) => {
          const count =
            k === "all"
              ? reviews.length
              : k === "featured"
                ? reviews.filter((r) => !!r.featured).length
                : reviews.filter((r) => r.status === k).length;
          return (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition ${
                filter === k
                  ? "bg-gold text-primary-foreground border-gold"
                  : "border-gold/20 text-foreground/80 hover:border-gold/50"
              }`}
            >
              {label} ({count})
            </button>
          );
        })}
      </div>

      {/* Main Table Panel */}
      <Panel className="border border-gold/20 bg-card/30">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gold border-t-transparent" />
            <span className="ml-3">Loading reviews...</span>
          </div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center text-center p-6">
            <AlertCircle className="h-10 w-10 text-destructive mb-3" />
            <h3 className="text-lg font-bold text-foreground">Failed to load reviews</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Please ensure the backend server is running and try again.
            </p>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-muted-foreground text-sm">
            No reviews found matching the selected filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gold/10 text-xs font-bold uppercase tracking-wider text-gold-soft bg-accent/25">
                  <th className="px-6 py-4">Customer</th>
                  <th className="px-6 py-4">Product</th>
                  <th className="px-6 py-4">Rating & Content</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold/10 text-sm">
                {filteredReviews.map((review) => {
                  const prod = review.productId;
                  const prodName =
                    typeof prod === "object" && prod
                      ? prod.name.en
                      : review.productSlug || "Unknown Product";
                  const prodImage = typeof prod === "object" && prod ? prod.image : undefined;

                  return (
                    <tr key={review._id} className="hover:bg-accent/10 transition-colors">
                      {/* Customer Info */}
                      <td className="px-6 py-5 vertical-top align-top">
                        <div className="font-semibold text-foreground">{review.userName}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {review.userLocation || "Unknown"}
                        </div>
                        <div className="text-[10px] text-muted-foreground/60 mt-1">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </div>
                      </td>

                      {/* Product Info */}
                      <td className="px-6 py-5 vertical-top align-top">
                        <div className="flex items-center gap-3">
                          {prodImage && (
                            <img
                              src={prodImage}
                              alt={prodName}
                              className="h-10 w-10 rounded-md object-cover border border-gold/25"
                            />
                          )}
                          <div>
                            <div className="font-medium text-foreground">{prodName}</div>
                            <div className="text-xs text-muted-foreground font-mono mt-0.5">
                              /{review.productSlug}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Rating & Content */}
                      <td className="px-6 py-5 vertical-top align-top max-w-md">
                        <div className="flex items-center gap-1 text-gold mb-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-3.5 w-3.5 ${
                                star <= review.rating
                                  ? "fill-gold text-gold"
                                  : "text-muted-foreground/35"
                              }`}
                            />
                          ))}
                        </div>
                        {review.title && (
                          <div className="font-semibold text-foreground mb-1">{review.title}</div>
                        )}
                        <div className="text-foreground/80 leading-relaxed break-words">
                          {review.body}
                        </div>
                        {review.photos && review.photos.length > 0 && (
                          <div className="flex gap-2 mt-3 flex-wrap">
                            {review.photos.map((photo, i) => (
                              <img
                                key={i}
                                src={photo}
                                alt="Review Attachment"
                                className="h-14 w-14 rounded-md object-cover border border-gold/15 hover:scale-105 transition-transform cursor-pointer"
                              />
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-5 vertical-top align-top">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${
                            review.status === "published"
                              ? "bg-emerald-500/15 text-emerald-400"
                              : review.status === "rejected"
                                ? "bg-rose-500/15 text-rose-400"
                                : "bg-amber-500/15 text-amber-400"
                          }`}
                        >
                          {review.status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-5 vertical-top align-top text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Approve/Publish Button */}
                          {review.status !== "published" && (
                            <button
                              onClick={() => handleStatusChange(review._id, "published")}
                              className="rounded-md border border-emerald-500/30 p-1.5 text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                              title="Publish Review"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          )}

                          {/* Reject Button */}
                          {review.status !== "rejected" && (
                            <button
                              onClick={() => handleStatusChange(review._id, "rejected")}
                              className="rounded-md border border-rose-500/30 p-1.5 text-rose-400 hover:bg-rose-500/10 transition-colors"
                              title="Reject Review"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}

                          {/* Toggle Featured Button */}
                          <button
                            onClick={() => handleToggleFeatured(review._id, !!review.featured)}
                            className={`rounded-md border p-1.5 transition-colors ${
                              review.featured
                                ? "border-pink-500 bg-pink-500/10 text-pink-400"
                                : "border-gold/30 text-gold-soft hover:bg-accent/40 hover:text-gold"
                            }`}
                            title={
                              review.featured ? "Unfeature Testimonial" : "Feature Testimonial"
                            }
                          >
                            <Heart
                              className={`h-3.5 w-3.5 ${review.featured ? "fill-current" : ""}`}
                            />
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDelete(review._id)}
                            className="rounded-md border border-gold/25 p-1.5 text-gold-soft hover:bg-rose-500/10 hover:text-rose-400 transition-colors"
                            title="Delete Permanently"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Add Review Modal Dialog */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-lg rounded-xl border border-gold/30 bg-brand-card p-6 shadow-2xl">
            <button
              onClick={() => {
                setIsOpen(false);
                resetForm();
              }}
              className="absolute top-4 right-4 rounded p-1 text-muted-foreground hover:text-gold"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="font-display text-2xl text-gold mb-2">Add Customer Review</h2>
            <p className="text-xs text-muted-foreground mb-6">
              Manually post a product review received offline or via other channels.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Select Product *
                </label>
                <select
                  required
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                >
                  <option value="" className="bg-brand-card text-foreground">
                    -- Choose a Fragrance --
                  </option>
                  {products.map((p) => (
                    <option key={p._id} value={p._id} className="bg-brand-card text-foreground">
                      {p.name.en} ({p.slug})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Reviewer Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="e.g. Adnan Rahman"
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Reviewer Location
                  </label>
                  <input
                    type="text"
                    value={userLocation}
                    onChange={(e) => setUserLocation(e.target.value)}
                    placeholder="e.g. Dhaka"
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 items-center">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Rating *
                  </label>
                  <div className="flex items-center gap-1.5 py-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className="text-gold hover:scale-110 transition-transform"
                      >
                        <Star
                          className={`h-6 w-6 ${star <= rating ? "fill-gold" : "text-muted-foreground/40"}`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Review Photo (Optional)
                  </label>
                  <label className="flex items-center gap-2 rounded border border-dashed border-gold/30 bg-accent/15 px-3 py-2 text-xs text-gold-soft cursor-pointer hover:border-gold hover:text-gold transition">
                    <Upload className="h-4 w-4" />
                    <span>{imageFile ? "Change photo" : "Upload photo"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>

              {imagePreview && (
                <div className="flex items-center gap-4 rounded border border-gold/10 bg-accent/5 p-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="h-12 w-12 rounded object-cover border border-gold/20"
                  />
                  <div className="flex-1 text-xs text-muted-foreground truncate">
                    {imageFile?.name}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                    }}
                    className="text-muted-foreground hover:text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Review Title (Optional)
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Absolutely brilliant scent!"
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Review Description *
                </label>
                <textarea
                  required
                  rows={4}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Describe the experience, performance, and notes..."
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold resize-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    resetForm();
                  }}
                  className="rounded border border-gold/20 px-4 py-2 text-xs font-semibold text-foreground hover:bg-accent/20 cursor-pointer"
                  disabled={isUploading || createMutation.isPending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 rounded bg-gold px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer"
                  disabled={isUploading || createMutation.isPending}
                >
                  {(isUploading || createMutation.isPending) && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  Save Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
